import _omit from "lodash/omit";

import {
  ActivityLogQCsV2,
  BonusDisputeFieldsI,
  BonusQualificationCriteriaTypeI,
  CampaignTiersI,
  MappedQCsAndPricingResponseI,
  QCsAndPricingResponseI,
  QCsBackendTextsI,
  QualificationCriteriaDisputeReasonsRequestI,
  QualificationCriteriaSurveyValuesI,
  QualificationCriteriaTableDisputeReasonsValueI,
  QualificationCriteriaTableTogglesValueI,
  QualificationCriteriaTypeWithBonusI,
  QualificationTierI,
} from "shared/lib/interfaces/qualification-criteria";
import {
  BONUS_2_QUALIFICATION_CRITERIA_TYPE,
  BONUS_CRITERIA_TYPE_TO_TIER,
  BONUS_QUALIFICATION_CRITERIA_TYPE,
  QUALIFICATION_CRITERIA_TYPE_MAP,
  QUALIFICATION_CRITERIA_TYPES,
  QUALIFICATION_TIER,
} from "shared/lib/constants/qualification-criteria";

export const getQCTextsFromResponse = (qcs: QCsBackendTextsI | undefined) => {
  if (!qcs) {
    return {
      companySizeCriteria: "",
      titleCriteria: "",
      industryCriteria: "",
      problemPainCriteria: "",
      timingBudgetCriteria: "",
      bonus2Criteria: "",
    };
  }

  return {
    companySizeCriteria: qcs.company_size_criteria || "",
    titleCriteria: qcs.title_criteria || "",
    industryCriteria: qcs.industry_criteria || "",
    problemPainCriteria: qcs.problem_pain_criteria || "",
    timingBudgetCriteria: qcs.timing_budget_criteria || "",
    bonus2Criteria: qcs.bonus_2_criteria || "",
  };
};

export const getQCsAndPricingFromResponse = (
  qcsAndPricingResponse: QCsAndPricingResponseI | undefined
): MappedQCsAndPricingResponseI => {
  if (!qcsAndPricingResponse) {
    return {
      baseChargeAmount: 0,
      bonusChargeAmount: 0,
      bonus2ChargeAmount: 0,
      companySizeCriteria: "",
      titleCriteria: "",
      industryCriteria: "",
      problemPainCriteria: "",
      timingBudgetCriteria: "",
      bonus2Criteria: "",
    };
  }

  return {
    baseChargeAmount:
      qcsAndPricingResponse.qualification_criteria?.base_charge_amount,
    bonusChargeAmount:
      qcsAndPricingResponse.qualification_criteria?.bonus_charge_amount,
    bonus2ChargeAmount:
      qcsAndPricingResponse.qualification_criteria?.bonus_2_charge_amount ?? 0,
    ...getQCTextsFromResponse(qcsAndPricingResponse.qualification_criteria),
  };
};

/* Single source of truth for which tiers a campaign offers.
 *
 * This replaces four separate, inconsistent derivations that had grown across
 * the apps: `!!bonusChargeAmount`, `!!timingBudgetCriteria`,
 * `!!campaign.timing_budget_criteria`, and — a genuine bug — the activity log
 * reading `is_timing_budget_met`, which is a RESULT rather than configuration,
 * so campaigns whose bonus was not met rendered as having no bonus at all.
 *
 * CRITERION TEXT IS THE AUTHORITY, not the charge amount. Two reasons:
 *
 *  1. The rep-facing pricing endpoint returns the rep's cut, which floors to
 *     the nearest $25 (MoneyToUserPayout). A small bonus can therefore arrive
 *     as 0 for a rep while the tier genuinely exists, which would hide the
 *     question and make the bonus unclaimable.
 *  2. The backend only ever writes criterion text when ops configures the
 *     tier, so empty text is a reliable "not offered".
 */
export const getCampaignTiers = (
  qcs:
    | Partial<
        Pick<
          MappedQCsAndPricingResponseI,
          "timingBudgetCriteria" | "bonus2Criteria"
        >
      >
    | undefined
    | null
): CampaignTiersI => {
  const hasBonus = Boolean(qcs?.timingBudgetCriteria);
  const hasBonus2 = Boolean(qcs?.bonus2Criteria);

  const bonusTypes: BonusQualificationCriteriaTypeI[] = [];
  if (hasBonus) bonusTypes.push(BONUS_QUALIFICATION_CRITERIA_TYPE);
  if (hasBonus2) bonusTypes.push(BONUS_2_QUALIFICATION_CRITERIA_TYPE);

  return {
    hasBonus,
    hasBonus2,
    bonusTypes,
    tierCount: 1 + bonusTypes.length,
  };
};

/* Labels the bonus rows. A campaign with a single bonus keeps saying "Bonus",
 * exactly as it does today; only a campaign offering both needs them numbered
 * so a rep can tell the two radio options apart.
 */
export const getBonusCriteriaLabel = (
  type: BonusQualificationCriteriaTypeI,
  tiers: CampaignTiersI
): string => {
  if (!tiers.hasBonus || !tiers.hasBonus2) {
    return "Bonus";
  }
  return type === BONUS_QUALIFICATION_CRITERIA_TYPE
    ? "Bonus Criteria 1"
    : "Bonus Criteria 2";
};

/* Resolves the tier a submission earned.
 *
 * Mirrors V2SubmitAESurveyRequest.ResolveTier in the Go backend and must stay
 * in step with it. The base criteria gate everything, and because the bonuses
 * are mutually exclusive the higher one wins if both somehow arrive true.
 */
export const getEarnedQualificationTier = (
  selectedQCs: QualificationCriteriaTableTogglesValueI | undefined,
  tiers: CampaignTiersI
): QualificationTierI => {
  const baseMet = QUALIFICATION_CRITERIA_TYPES.every((qc) => selectedQCs?.[qc]);
  if (!baseMet) {
    return QUALIFICATION_TIER.BASE;
  }

  // Walk highest tier first so bonus 2 takes priority.
  const earned = [...tiers.bonusTypes]
    .reverse()
    .find((type) => selectedQCs?.[type]);

  return earned ? BONUS_CRITERIA_TYPE_TO_TIER[earned] : QUALIFICATION_TIER.BASE;
};

export const mapSelectedQCsToRequest = (
  selectedQCs: QualificationCriteriaTableTogglesValueI | undefined
): QualificationCriteriaSurveyValuesI => ({
  size_met:
    selectedQCs?.[QUALIFICATION_CRITERIA_TYPE_MAP.COMPANY_SIZE] ?? false,
  title_met:
    selectedQCs?.[QUALIFICATION_CRITERIA_TYPE_MAP.PROSPECT_TITLE] ?? false,
  industry_met:
    selectedQCs?.[QUALIFICATION_CRITERIA_TYPE_MAP.COMPANY_INDUSTRY] ?? false,
  problem_met: selectedQCs?.[QUALIFICATION_CRITERIA_TYPE_MAP.PROBLEM] ?? false,
  timing_met: selectedQCs?.[BONUS_QUALIFICATION_CRITERIA_TYPE] ?? false,
  bonus_2_met: selectedQCs?.[BONUS_2_QUALIFICATION_CRITERIA_TYPE] ?? false,
});

export const mapActivityLogQCToSelectedQCs = (
  backendCheckedQCs: ActivityLogQCsV2 | undefined
): QualificationCriteriaTableTogglesValueI => ({
  [QUALIFICATION_CRITERIA_TYPE_MAP.COMPANY_SIZE]:
    backendCheckedQCs?.is_size_met || false,
  [QUALIFICATION_CRITERIA_TYPE_MAP.PROSPECT_TITLE]:
    backendCheckedQCs?.is_title_met || false,
  [QUALIFICATION_CRITERIA_TYPE_MAP.COMPANY_INDUSTRY]:
    backendCheckedQCs?.is_industry_met || false,
  [QUALIFICATION_CRITERIA_TYPE_MAP.PROBLEM]:
    backendCheckedQCs?.is_problem_pain_met || false,
  [BONUS_QUALIFICATION_CRITERIA_TYPE]:
    backendCheckedQCs?.is_timing_budget_met || false,
  [BONUS_2_QUALIFICATION_CRITERIA_TYPE]:
    backendCheckedQCs?.is_bonus_2_met || false,
});

/* "Did this meeting qualify at all", ignoring bonuses. Omitting every bonus
 * key by name keeps this correct as tiers are added; the previous version
 * omitted only "timing_met" and would have silently required bonus 2.
 */
export const checkIfSelectedQCsQualify = (
  selectedQCs: QualificationCriteriaSurveyValuesI
) =>
  Object.values(_omit(selectedQCs, ["timing_met", "bonus_2_met"])).every(
    Boolean
  );

/* "Did this meeting earn a bonus", i.e. base plus at least one bonus. The
 * bonuses are mutually exclusive, so this is an OR across them rather than
 * the old "every value is true".
 */
export const checkIfSelectedQCsQualifyWithBonus = (
  selectedQCs: QualificationCriteriaSurveyValuesI
) =>
  checkIfSelectedQCsQualify(selectedQCs) &&
  Boolean(selectedQCs.timing_met || selectedQCs.bonus_2_met);

/**
 * This function used to convert disputes entered by the user to the request of
 * submit AE survey v2.
 *
 * @param disputeReasons
 */
export const mapDisputeReasonsToRequest = (
  disputeReasons: QualificationCriteriaTableDisputeReasonsValueI | undefined
): QualificationCriteriaDisputeReasonsRequestI => ({
  reason_size_not_met: disputeReasons?.companySizeCriteria || null,
  reason_title_not_met: disputeReasons?.titleCriteria || null,
  reason_industry_not_met: disputeReasons?.industryCriteria || null,
  reason_problem_not_met: disputeReasons?.problemPainCriteria || null,
  reason_timing_not_met: disputeReasons?.timingBudgetCriteria || null,
  reason_bonus_2_not_met: disputeReasons?.bonus2Criteria || null,
});

export const checkIfQCAuditQualified = (
  audit: ActivityLogQCsV2 | null | undefined
) => {
  if (!audit) {
    return false;
  }

  return (
    audit?.is_size_met &&
    audit?.is_title_met &&
    audit?.is_industry_met &&
    audit?.is_problem_pain_met
  );
};

export const DISPUTE_REASON_MIN_LENGTH = 5;

export const checkIfDisputeReasonValid = (reason: string | null | undefined) =>
  !!reason && reason.length >= DISPUTE_REASON_MIN_LENGTH;

/* The Next-button gate for a dispute-mode qualification form: is there a
 * criterion the pre-check confirmed, the AE unticked, and no valid reason was
 * given for?
 *
 * Iterates the criteria the campaign actually has - the four base ones plus
 * whatever tiers.bonusTypes says - NOT the keys of the reasons object. The
 * reasons state starts without bonus keys (they appear on first input), so
 * keying off it silently exempted every untouched bonus from needing a reason.
 *
 * A bonus that was auto-cleared because a base criterion failed is exempt:
 * the base failure already explains it, and its own dispute input is not even
 * rendered (see checkIfShouldRenderDisputeInput).
 */
export const checkIfAnyDisputeReasonMissing = ({
  selectedQCs,
  disputeReasons,
  preCheckedQCs,
  tiers,
}: {
  selectedQCs: QualificationCriteriaTableTogglesValueI | undefined;
  disputeReasons:
    | Partial<QualificationCriteriaTableDisputeReasonsValueI>
    | undefined;
  preCheckedQCs: QualificationCriteriaTableTogglesValueI | null | undefined;
  tiers: CampaignTiersI;
}): boolean => {
  if (!preCheckedQCs) {
    return false;
  }

  const isBaseQualified = checkIfSelectedQCsQualify(
    mapSelectedQCsToRequest(selectedQCs)
  );

  const allQCTypes: QualificationCriteriaTypeWithBonusI[] = [
    ...QUALIFICATION_CRITERIA_TYPES,
    ...tiers.bonusTypes,
  ];

  return allQCTypes.some((qcType) => {
    const isReasonMissing =
      !checkIfDisputeReasonValid(disputeReasons?.[qcType]) &&
      !!preCheckedQCs[qcType] &&
      !selectedQCs?.[qcType];

    if ((tiers.bonusTypes as readonly string[]).includes(qcType)) {
      return isBaseQualified && isReasonMissing;
    }

    return isReasonMissing;
  });
};

/* The bonus part of the "Potential payout + bonus" display, in the same cents
 * the pricing endpoint returns the charge amounts in. Base earns no bonus.
 */
export const getBonusChargeAmountForTier = (
  tier: QualificationTierI,
  pricing:
    | Pick<
        MappedQCsAndPricingResponseI,
        "bonusChargeAmount" | "bonus2ChargeAmount"
      >
    | undefined
): number => {
  switch (tier) {
    case QUALIFICATION_TIER.BONUS:
      return pricing?.bonusChargeAmount ?? 0;
    case QUALIFICATION_TIER.BONUS_2:
      return pricing?.bonus2ChargeAmount ?? 0;
    default:
      return 0;
  }
};

/* The bonus half of a dispute payload, one met/reason pair per bonus the
 * campaign offers.
 *
 * A bonus is CONTESTED (met=false + the customer's reason) only when it was
 * credited to the meeting AND the customer unticked it while the base
 * criteria still hold. Everything else is "not applicable" (null/null): a
 * bonus the meeting never earned is not being disputed just because it is
 * unticked, and a credited bonus auto-cleared by a failed base criterion is
 * already explained by that base dispute. Sending false without a reason for
 * either of those makes the backend reject the whole submission.
 */
export const mapBonusDisputeFieldsToRequest = ({
  selectedQCs,
  creditedQCs,
  disputeReasons,
  tiers,
}: {
  selectedQCs: QualificationCriteriaTableTogglesValueI | undefined;
  creditedQCs: QualificationCriteriaTableTogglesValueI | null | undefined;
  disputeReasons:
    | Partial<QualificationCriteriaTableDisputeReasonsValueI>
    | undefined;
  tiers: CampaignTiersI;
}): BonusDisputeFieldsI => {
  const fieldNames: Record<
    BonusQualificationCriteriaTypeI,
    {
      isMet: "is_timing_budget_met" | "is_bonus_2_met";
      reason: "reason_timing_budget_not_met" | "reason_bonus_2_not_met";
    }
  > = {
    [BONUS_QUALIFICATION_CRITERIA_TYPE]: {
      isMet: "is_timing_budget_met",
      reason: "reason_timing_budget_not_met",
    },
    [BONUS_2_QUALIFICATION_CRITERIA_TYPE]: {
      isMet: "is_bonus_2_met",
      reason: "reason_bonus_2_not_met",
    },
  };

  const isBaseStillMet = checkIfSelectedQCsQualify(
    mapSelectedQCsToRequest(selectedQCs)
  );

  const fields: BonusDisputeFieldsI = {};

  for (const type of tiers.bonusTypes) {
    const { isMet, reason } = fieldNames[type];

    if (selectedQCs?.[type]) {
      fields[isMet] = true;
      fields[reason] = null;

      continue;
    }

    const isContested = !!creditedQCs?.[type] && isBaseStillMet;

    fields[isMet] = isContested ? false : null;
    fields[reason] = isContested ? (disputeReasons?.[type] ?? null) : null;
  }

  return fields;
};

/* Runtime narrowing for tier strings that arrive over the wire typed as plain
 * string. A cast would silently accept a tier this build knows nothing about;
 * callers should decide explicitly what to do with one.
 */
export const isQualificationTier = (
  value: unknown
): value is QualificationTierI =>
  typeof value === "string" &&
  (Object.values(QUALIFICATION_TIER) as string[]).includes(value);
